/**
 * @module linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/realignColumnSortShared
 * @description column_sort 与 QB 指区/维区对齐 — 共享键、列表切片与签名工具
 */

import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { fieldKey, snapshotList } from '../../../fieldSnapshot';
import type { IFieldSnapshot } from '../../../types';
import { isMetricAttachedVirtualColumn } from '../untils/fieldUtils';
import { isDimPctColumnForMetric } from '../untils/dimensionPercentage';
import {
  getColumnSortItemId,
  isColumnSortDimension,
  isCompareUserFilterColumn,
  normalizeColumnSortItem,
  stripPercentagePrefix,
} from '../untils/keys';

/** 防止异常数据导致死循环 */
export const MAX_ALIGN_ITERATIONS = 64;

/** 指区对齐用的稳定键：table_code + 物理列名（不含 table_id） */
export function getMetricReorderKey(item: IColumnSortItem): string {
  const col = stripPercentagePrefix(item.column || '');
  return fieldKey({
    table_code: item.table_code || 't0',
    column_name: col,
  } as IFieldSnapshot);
}

/** 维区对齐用的稳定键：table_code + 列名 */
export function getDimensionReorderKey(item: IColumnSortItem): string {
  return fieldKey({
    table_code: item.table_code || 't0',
    column_name: item.column || '',
  } as IFieldSnapshot);
}

function isStandaloneCompareTimeColumn(item: IColumnSortItem): boolean {
  if (isColumnSortDimension(item)) {
    return false;
  }
  return item.is_compare_date === true;
}

/** 是否为 dimension 且可能出现在 QB 维区联动中 */
export function isQBDimensionColumn(item: IColumnSortItem): boolean {
  if (!isColumnSortDimension(item)) {
    return false;
  }
  if (isCompareUserFilterColumn(item.column)) {
    return false;
  }
  if (isStandaloneCompareTimeColumn(item)) {
    return false;
  }
  return true;
}

function qbDimensionKeySet(columnDimensions: unknown[]): Set<string> {
  const keys = new Set<string>();
  for (const snap of snapshotList(columnDimensions, 'dimension')) {
    keys.add(fieldKey(snap));
  }
  return keys;
}

/** column_sort 左→右：参与指区对齐的主指标（跳过维与 % 列） */
export function listMainMetricItemsInOrder(
  list: IColumnSortItem[],
): IColumnSortItem[] {
  const out: IColumnSortItem[] = [];
  for (const raw of list) {
    const item = normalizeColumnSortItem(raw);
    if (isColumnSortDimension(item) || isMetricAttachedVirtualColumn(item)) {
      continue;
    }
    out.push(item);
  }
  return out;
}

export function listQBSyncedDimensionItemsInOrder(
  list: IColumnSortItem[],
  columnDimensions: unknown[],
): IColumnSortItem[] {
  const qbKeys = qbDimensionKeySet(columnDimensions);
  const out: IColumnSortItem[] = [];
  for (const raw of list) {
    const item = normalizeColumnSortItem(raw);
    if (!isQBDimensionColumn(item)) {
      continue;
    }
    if (qbKeys.has(getDimensionReorderKey(item))) {
      out.push(item);
    }
  }
  return out;
}

export function findMetricSnapshot(
  item: IColumnSortItem,
  columnMetrics: unknown[],
): IFieldSnapshot | null {
  const want = getMetricReorderKey(item);
  for (const snap of snapshotList(columnMetrics, 'metric')) {
    if (fieldKey(snap) === want) {
      return snap;
    }
  }
  return null;
}

export function mergeSnapshotWithSortItem(
  snap: IFieldSnapshot,
  item: IColumnSortItem,
): IFieldSnapshot {
  return {
    ...snap,
    table_id: item.table_id ?? snap.table_id,
    table_code: item.table_code || snap.table_code || 't0',
  };
}

export function isMainMetricItem(item: IColumnSortItem): boolean {
  const norm = normalizeColumnSortItem(item);
  return !isColumnSortDimension(norm) && !isMetricAttachedVirtualColumn(norm);
}

export function dedupeList(list: IColumnSortItem[]): IColumnSortItem[] {
  const seen = new Set<string>();
  const out: IColumnSortItem[] = [];
  for (const raw of list) {
    const item = normalizeColumnSortItem(raw);
    const id = getColumnSortItemId(item);
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(item);
  }
  return out;
}

export function getColumnSortOrderSignature(list: IColumnSortItem[]): string {
  return list.map(item => getColumnSortItemId(item)).join('|');
}
